import type { FormElement, TableElement } from '../model/form.types';
import { fieldLabel } from './definitions/shared';
import { currentValue, migratedElements, snapshotFields } from './field-walk';
import { TABLE_APPEND_FORBIDDEN_MESSAGE, TABLE_NEW_ROWS_LIMIT_MESSAGE } from './accept-answers';
import { TABLE_NEW_ROWS_LIMIT } from './limits';
import { extraCellNodeId, modelRowColumns, type ModelRowColumn } from './model-row';
import { resolveFieldAccess, type FieldAccessResolver } from './permissions';
import { defaultPatternTest } from './pattern-test';
import { getDefinition, getFieldDefinition } from './registry';
import { evaluateRules, type RuleOutcome } from './rules';
import { pageOrientation } from './page-orientation';
import { isCoveredCell } from './table-cell';
import { hasVisibleContent } from './visibility';
import type {
  CompileInput,
  CompileTitles,
  CompiledCell,
  CompiledExtraRow,
  CompiledExtraRowCell,
  CompiledFieldNode,
  ControlSpec,
  CompiledForm,
  CompiledNode,
  CompiledTableNode,
  FieldDefinition,
  FieldValue,
  MatrixAnswers,
  ValidateContext,
  ValidationIssue,
} from '../model/types';

interface CompileContext {
  answers: MatrixAnswers;
  titles: CompileTitles;
  ruleOutcome: (fieldId: string) => RuleOutcome;
  access: FieldAccessResolver;
  validation: ValidateContext;
  frozenIds: Set<string>;
  validatedIds: Set<string> | null;
  frozenExtraRowCounts: Readonly<Record<string, number>>;
  reopenHistoricalRows: boolean;
  reopenedExtraRows: Record<string, ReadonlySet<number>>;
  anchorIds: Set<string>;
  reportIds: Set<string>;
  fields: Record<string, CompiledFieldNode>;
  fieldOrder: string[];
  extraCellIssues: ValidationIssue[];
  multipleTableIds: Set<string>;
}

// The blob keeps the value of a hidden or locked field, so a value of the wrong shape is
// reported wherever the field is; required, options and limits bind only who can fill it now.
function fieldIssues(
  definition: FieldDefinition,
  element: FormElement,
  value: FieldValue,
  active: boolean,
  validation: ValidateContext,
): ValidationIssue[] {
  if (active) return definition.validate(element, value, validation);
  const shape = definition.shapeIssue(element, value);
  return shape ? [shape] : [];
}

function compileField(element: FormElement, context: CompileContext): CompiledFieldNode | null {
  const definition = getFieldDefinition(element);
  if (!definition) return null;

  const value = currentValue(element, context.answers);
  const rule = context.ruleOutcome(element.id);
  const access = context.access(element.id);
  const required = element.properties.required === true;
  // A frozen field keeps the value of an earlier response, so required, options and limits would
  // demand from this subject what nobody can change any more; only its shape is checked.
  const editable = access.editable && !context.frozenIds.has(element.id);
  const validated = context.validatedIds?.has(element.id) ?? true;
  const active = rule.visible && rule.enabled && editable && validated;

  const node: CompiledFieldNode = {
    kind: 'field',
    id: element.id,
    type: definition.type,
    label: fieldLabel(element),
    labelPosition: element.properties.labelPosition ?? 'top',
    value,
    state: {
      visible: rule.visible,
      hiddenByRule: !rule.visible,
      enabled: rule.enabled,
      editable,
      required,
    },
    tags: { anchor: context.anchorIds.has(element.id), report: context.reportIds.has(element.id) },
    control: definition.control(element),
    issues: fieldIssues(definition, element, value, active, context.validation),
    element,
  };

  context.fields[element.id] = node;
  context.fieldOrder.push(element.id);
  return node;
}

// An added row never inherits the builder default ("Valor"): emptyValue returns that default for
// text and number fields, because it belongs to the model row's own answer, and a copy born
// pre-filled would be stored without anyone typing it.
function blankCellValue(control: ControlSpec): FieldValue {
  switch (control.kind) {
    case 'number':
      return null;
    case 'checkbox':
      return [];
    case 'select':
      return control.multiple ? [] : '';
    default:
      return '';
  }
}

function compileExtraCell(
  column: ModelRowColumn & { element: FormElement; definition: FieldDefinition },
  row: Record<number, FieldValue>,
  rowIndex: number,
  historical: boolean,
  tableId: string,
  context: CompileContext,
): CompiledFieldNode {
  const { element, definition, columnIndex } = column;
  const control = definition.control(element);
  const stored = row[columnIndex];
  const value = stored === undefined ? blankCellValue(control) : stored;
  const rule = context.ruleOutcome(element.id);
  const access = context.access(element.id);
  const id = extraCellNodeId(tableId, rowIndex, columnIndex);
  // A historical row was accepted by an earlier response; like a frozen field, only its shape is
  // checked, because nobody but its author or a correction can change it any more.
  const reopened =
    historical &&
    (context.reopenHistoricalRows || context.reopenedExtraRows[tableId]?.has(rowIndex) === true);
  const editable = access.editable && (!historical || reopened);
  const validated = !reopened || (context.validatedIds?.has(id) ?? true);
  const active = rule.visible && rule.enabled && editable && validated;

  const node: CompiledFieldNode = {
    kind: 'field',
    id,
    type: definition.type,
    label: fieldLabel(element),
    labelPosition: element.properties.labelPosition ?? 'top',
    value,
    state: {
      visible: rule.visible,
      hiddenByRule: !rule.visible,
      enabled: rule.enabled,
      editable,
      required: element.properties.required === true,
    },
    tags: { anchor: false, report: false },
    control,
    issues: fieldIssues(definition, element, value, active, context.validation).map((issue) => ({
      ...issue,
      cell: { tableId, rowIndex, columnIndex },
    })),
    element,
  };

  context.extraCellIssues.push(...node.issues);
  return node;
}

// A column takes a value in an added row only when the subject edits it and no rule hides or
// disables it; with none left, an added row could only stay empty and turn frozen for good.
function canAppendTo(element: TableElement, context: CompileContext): boolean {
  if (element.properties.is_multiple !== true) return false;
  return modelRowColumns(element).some((column) => {
    if (column.element === null) return false;
    const rule = context.ruleOutcome(column.element.id);
    return context.access(column.element.id).editable && rule.visible && rule.enabled;
  });
}

function compileExtraRows(element: TableElement, context: CompileContext, canAppend: boolean): CompiledExtraRow[] {
  if (element.properties.is_multiple !== true) return [];
  const columns = modelRowColumns(element);
  const rows = context.answers.tables[element.id]?.extraRows ?? [];
  const historicalCount = context.frozenExtraRowCounts[element.id] ?? 0;

  return rows.map((row, rowIndex) => {
    const historical = rowIndex < historicalCount;
    if (!historical && !canAppend) {
      context.extraCellIssues.push({
        fieldId: element.id,
        code: 'type',
        message: TABLE_APPEND_FORBIDDEN_MESSAGE,
        cell: { tableId: element.id, rowIndex },
      });
    } else if (!historical && rowIndex >= historicalCount + TABLE_NEW_ROWS_LIMIT) {
      // Unreachable from the sheet, whose plus stops at the limit, and from the server, whose body
      // shape refuses more rows before the engine runs; it keeps a hand-built input honest.
      context.extraCellIssues.push({
        fieldId: element.id,
        code: 'type',
        message: TABLE_NEW_ROWS_LIMIT_MESSAGE,
        cell: { tableId: element.id, rowIndex },
      });
    }
    const cells: CompiledExtraRowCell[] = columns.map((column) => ({
      columnIndex: column.columnIndex,
      colSpan: column.colSpan,
      node:
        column.element && column.definition
          ? compileExtraCell(
              { ...column, element: column.element, definition: column.definition },
              row,
              rowIndex,
              historical,
              element.id,
              context,
            )
          : null,
    }));
    return { rowIndex, historical, cells };
  });
}

function compileTable(element: TableElement, context: CompileContext): CompiledTableNode {
  // Normalize and accept read the first table of a duplicated id; a later one must not
  // read the same rows with other columns, or it refuses a row the others accepted.
  const ownsRows = element.properties.is_multiple === true && !context.multipleTableIds.has(element.id);
  if (ownsRows) context.multipleTableIds.add(element.id);
  const canAppend = ownsRows && canAppendTo(element, context);
  const cells: CompiledCell[][] = (element.properties.cells ?? []).map((row) =>
    row.map((cell) => ({
      id: cell.id,
      rowIndex: cell.rowIndex,
      colIndex: cell.colIndex,
      rowSpan: cell.rowSpan,
      colSpan: cell.colSpan,
      hidden: isCoveredCell(cell),
      backgroundColor: cell.properties?.backgroundColor,
      padding: cell.properties?.padding,
      alignment: cell.properties?.alignment,
      node: cell.content ? compileElement(cell.content, context) : null,
      source: cell,
    })),
  );

  return {
    kind: 'table',
    id: element.id,
    type: 'TABLE',
    rows: element.properties.rows,
    columns: element.properties.columns,
    columnWidths: element.properties.columnWidths,
    rowHeights: element.properties.rowHeights,
    // Legacy matrices store allowAddRows: true on every table; it never means a multiple table.
    allowAddRows: element.properties.allowAddRows === true,
    isMultiple: element.properties.is_multiple === true,
    canAppend,
    cells,
    extraRows: ownsRows ? compileExtraRows(element, context, canAppend) : [],
    visible: hasVisibleContent({ cells }),
    element,
  };
}

function compileElement(element: FormElement, context: CompileContext): CompiledNode {
  const definition = getDefinition(element);
  if (!definition) return { kind: 'unsupported', id: element.id, type: element.type };

  switch (definition.kind) {
    case 'header':
      return definition.compile(element);
    case 'static':
      return {
        ...definition.compile(element, { titles: context.titles }),
        visible: context.ruleOutcome(element.id).visible,
      };
    case 'image':
      return { ...definition.compile(element), visible: context.ruleOutcome(element.id).visible };
    case 'container':
      return compileTable(element as TableElement, context);
    case 'field':
      return compileField(element, context) ?? { kind: 'unsupported', id: element.id, type: element.type };
    default: {
      const exhaustive: never = definition;
      return exhaustive;
    }
  }
}

export function compileMatrix(input: CompileInput): CompiledForm {
  const elements = migratedElements(input.structure);
  const context: CompileContext = {
    answers: input.answers,
    titles: input.titles ?? {},
    ruleOutcome: evaluateRules(input.rules, snapshotFields(elements, input.answers)),
    access: resolveFieldAccess(input.permissions, input.subject, input.options?.defaultAccess),
    validation: { patternTest: input.options?.patternTest ?? defaultPatternTest },
    frozenIds: new Set(input.options?.frozenFieldIds ?? []),
    validatedIds: input.options?.validatedFieldIds ? new Set(input.options.validatedFieldIds) : null,
    frozenExtraRowCounts: input.options?.frozenExtraRowCounts ?? {},
    reopenHistoricalRows: input.options?.reopenHistoricalRows === true,
    reopenedExtraRows: Object.fromEntries(
      Object.entries(input.options?.reopenedExtraRows ?? {}).map(([tableId, rows]) => [tableId, new Set(rows)]),
    ),
    anchorIds: new Set(input.rules?.anchorFieldIds ?? []),
    reportIds: new Set(input.rules?.reportFieldIds ?? []),
    fields: {},
    fieldOrder: [],
    extraCellIssues: [],
    multipleTableIds: new Set(),
  };

  const nodes = elements.map((element) => compileElement(element, context));
  const issues = [
    ...context.fieldOrder.flatMap((fieldId) => context.fields[fieldId].issues),
    ...context.extraCellIssues,
  ];

  return {
    orientation: pageOrientation(input.structure),
    nodes,
    fields: context.fields,
    fieldOrder: context.fieldOrder,
    issues,
  };
}
